import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ESTAGIO_LABEL, ESTAGIOS, type Estagio } from '../schemas';

type Tone = 'danger' | 'warn' | 'ok' | 'info' | 'accent';

// Avanco = acao principal (coral): seta direcional + posicao mais a direita no
// rodape. Desfechos terminais/laterais (Negado, Congelado) nao levam seta.
const TONS_AVANCO = new Set<Tone>(['ok', 'accent']);

interface Target {
  estagio: Estagio;
  tone: Tone;
}

// Pra cada estágio: destinos disponíveis no painel — avançar, lateral (congelar)
// e fechar. Forms inline (triagem, abordagem, proposta) continuam ativos pra
// captura de dados; o painel atalha a movimentação direta.
const TARGETS_POR_ESTAGIO: Record<Estagio, Target[]> = {
  sinal_detectado: [
    { estagio: 'qualificacao', tone: 'ok' },
    { estagio: 'fechado_perdido', tone: 'danger' },
  ],
  triagem: [{ estagio: 'qualificacao', tone: 'ok' }],
  qualificacao: [
    { estagio: 'abordagem', tone: 'ok' },
    { estagio: 'fechado_perdido', tone: 'danger' },
  ],
  falso_positivo_servico: [{ estagio: 'qualificacao', tone: 'accent' }],
  dossie: [{ estagio: 'abordagem', tone: 'ok' }],
  abordagem: [{ estagio: 'proposta', tone: 'ok' }],
  proposta: [
    { estagio: 'fechado_ganho', tone: 'ok' },
    { estagio: 'congelado', tone: 'info' },
  ],
  congelado: [{ estagio: 'proposta', tone: 'ok' }],
  fechado_ganho: [{ estagio: 'congelado', tone: 'info' }],
  fechado_perdido: [{ estagio: 'congelado', tone: 'info' }],
  recusado: [{ estagio: 'congelado', tone: 'info' }],
  descartado: [],
};

const ESTAGIO_ANTERIOR: Record<Estagio, Estagio | null> = {
  sinal_detectado: null,
  triagem: 'sinal_detectado',
  qualificacao: 'sinal_detectado',
  falso_positivo_servico: 'qualificacao',
  dossie: 'qualificacao',
  abordagem: 'qualificacao',
  proposta: 'abordagem',
  congelado: 'proposta',
  fechado_ganho: 'proposta',
  fechado_perdido: 'proposta',
  recusado: 'triagem',
  descartado: 'sinal_detectado',
};

// Avancar = acao principal = coral solido. Encerrar = variante danger do DS.
// Verde/vermelho chapado foi reprovado (mesma decisao das colunas Aprovado e
// Negado do kanban): no ARA verde e' etiqueta de status, nao botao.
const TONE_STYLES: Record<Tone, string> = {
  danger:
    'bg-[rgba(220,38,38,0.06)] text-[var(--danger)] border border-[rgba(220,38,38,0.18)] hover:bg-[rgba(220,38,38,0.12)]',
  warn:
    'bg-[rgba(234,179,8,0.12)] text-[#a16207] dark:text-[#facc15] hover:bg-[rgba(234,179,8,0.18)]',
  ok: 'bg-accent text-[var(--on-solid)] hover:bg-[var(--accent2)]',
  info: 'bg-surface2 text-text-2 hover:text-text',
  accent: 'bg-accent text-[var(--on-solid)] hover:bg-[var(--accent2)]',
};

interface Props {
  estagio: Estagio;
  onMove: (proximo: Estagio) => void;
  disabled?: boolean;
  variant?: 'aside' | 'inline';
}

export function MoverFasePanel({ estagio, onMove, disabled, variant = 'aside' }: Props) {
  const targets = TARGETS_POR_ESTAGIO[estagio];
  const anterior = ESTAGIO_ANTERIOR[estagio];

  if (targets.length === 0 && !anterior) return null;

  const idxAtual = ESTAGIOS.indexOf(estagio);
  const inline = variant === 'inline';

  // Figma: no rodape o avanco (coral) e o ultimo (canta direita); o desfecho
  // negativo vem a esquerda. No aside vertical mantem a ordem natural.
  const alvos = inline
    ? [...targets].sort(
        (a, b) => Number(TONS_AVANCO.has(a.tone)) - Number(TONS_AVANCO.has(b.tone)),
      )
    : targets;

  const Wrapper = inline
    ? (props: { children: ReactNode }) => (
        <div className="flex items-center gap-2">{props.children}</div>
      )
    : (props: { children: ReactNode }) => (
        <aside className="rounded-xl border border-border bg-elevated p-4 flex flex-col gap-3">
          <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
            Mover card para fase
          </h3>
          {props.children}
        </aside>
      );

  return (
    <Wrapper>
      {targets.length > 0 && (
        <div className={inline ? 'flex items-center gap-2' : 'flex flex-col gap-2'}>
          {alvos.map((t) => {
            const isBackward = ESTAGIOS.indexOf(t.estagio) < idxAtual;
            const isAvanco = TONS_AVANCO.has(t.tone);
            const Arrow = isBackward ? ArrowLeft : ArrowRight;
            return (
              <button
                key={t.estagio}
                type="button"
                disabled={disabled}
                onClick={() => onMove(t.estagio)}
                className={cn(
                  'group flex items-center justify-center gap-2 rounded-full px-3.5 py-2',
                  'text-[0.82rem] font-medium transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  isBackward && isAvanco && 'flex-row-reverse',
                  TONE_STYLES[t.tone],
                )}
              >
                <span>{ESTAGIO_LABEL[t.estagio]}</span>
                {isAvanco && (
                  <Arrow size={14} strokeWidth={1.75} className="opacity-70 group-hover:opacity-100" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {anterior && !inline && (
        <>
          {targets.length > 0 && (
            <div className={inline ? 'w-px self-stretch bg-border mx-1' : 'h-px bg-border my-1'} />
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => onMove(anterior)}
            className={cn(
              'flex items-center justify-center gap-2 rounded-full border border-border bg-elevated',
              'px-3.5 py-2 text-[0.82rem] font-medium text-text-2',
              'hover:bg-surface2 hover:text-text transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            title={`Voltar para ${ESTAGIO_LABEL[anterior]}`}
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            <span>{ESTAGIO_LABEL[anterior]}</span>
          </button>
        </>
      )}
    </Wrapper>
  );
}
